/**
 * `PlanAndUsageSection` — ADR-0263 S3 merchant read-only view of each
 * site's plan assignment and current-month usage.
 *
 * Hidden entirely while every site is `plan_source: 'none'` (the
 * default while the v2 catalog is draft) AND for `reviewer` /
 * `viewer` roles (plan and usage are billing data; same role floor
 * as /v1/billing). Meter bars: thin, lime under 80%, amber 80–99%,
 * coral at/over 100%; the over-limit copy says nothing is blocked.
 * S5 adds the "upgrade / add a pack" buttons; S3 is strictly read.
 */

import { useEffect, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import {
  getPlanUsage,
  type PlanUsageMeter,
  type PlanUsageResponse,
  type PlanUsageSite,
} from '../../api/plan-usage.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { Card } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

const DIMENSION_LABEL: Readonly<Record<PlanUsageMeter['dimension'], string>> = {
  page_loads_monthly: 'Page loads',
  orders_monthly: 'Orders',
  assistant_requests_monthly: 'AI assistant requests',
};

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly data: PlanUsageResponse }
  | { readonly kind: 'error' };

export function PlanAndUsageSection(): JSX.Element | null {
  const http = useOptionalHttp();
  // Optional: Billing renders in tests without an AuthProvider; no auth = hidden.
  const auth = useOptionalAuth();
  const role =
    auth?.state.kind === 'authenticated' ? auth.state.user.role : undefined;
  const canSee = role === 'owner' || role === 'admin';
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (http === null || !canSee) return;
    let cancelled = false;
    void getPlanUsage(http).then((res) => {
      if (cancelled) return;
      if (res.kind === 'ok') setState({ kind: 'ready', data: res.data });
      else setState({ kind: 'error' });
    });
    return () => {
      cancelled = true;
    };
  }, [http, canSee]);

  // Plan + usage are billing data. Reviewers and viewers never see them.
  if (!canSee) return null;
  if (state.kind === 'loading') return null;
  if (state.kind === 'error') return null;
  if (!state.data.active) return null;

  const agency_name = state.data.account.agency?.agency_name ?? null;
  return (
    <section id="plan-and-usage" style={{ marginTop: 24 }}>
      <SectionHeader />
      <div style={{ display: 'grid', gap: 16 }}>
        {state.data.sites.map((site) =>
          site.plan_source === 'none' ? null : (
            <SiteCard
              key={site.site_id}
              site={site}
              agency_name={agency_name}
            />
          ),
        )}
        {/* ADR §2.3 — ONE account-level assistant-requests reading,
            rendered after the per-site cards. Hidden when the account
            is not on v2 yet (`assistant_requests: null`). */}
        {state.data.account.assistant_requests !== null ? (
          <AccountAssistantCard meter={state.data.account.assistant_requests} />
        ) : null}
      </div>
    </section>
  );
}

function AccountAssistantCard({ meter }: { meter: PlanUsageMeter }): JSX.Element {
  const C = useC();
  return (
    <Card className="p-5 sm:p-6">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 20,
            color: C.text,
            letterSpacing: -0.3,
          }}
        >
          AI assistant requests
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          ACROSS YOUR SITES
        </div>
      </div>
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          marginTop: 6,
          lineHeight: 1.5,
        }}
      >
        Counts assistant requests across every site on your account. The cap
        is the most generous of your site plans.
      </p>
      <div style={{ marginTop: 12 }}>
        <MeterRow meter={meter} />
      </div>
    </Card>
  );
}

function SectionHeader(): JSX.Element {
  const C = useC();
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 6,
        }}
      >
        PLAN AND USAGE
      </div>
      <div
        style={{
          fontFamily: serifFont,
          fontSize: 24,
          color: C.text,
          letterSpacing: -0.5,
          lineHeight: 1,
        }}
      >
        Your sites this month
      </div>
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.5,
          marginTop: 6,
        }}
      >
        Each site's plan and what it has used this month. Numbers reset on
        the first of the month.
      </p>
    </div>
  );
}

function SiteCard({
  site,
  agency_name,
}: {
  site: PlanUsageSite;
  agency_name: string | null;
}): JSX.Element {
  const C = useC();
  // ADR §2.6 — agency-paid sites get a "Paid by <agency name>" label
  // and no upgrade / pack suggestions (S5 will suppress the buy
  // buttons on this branch; S3 has none yet, so this is just the
  // label today).
  const is_agency_paid = site.plan_source === 'agency';
  return (
    <Card className="p-5 sm:p-6">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 20,
            color: C.text,
            letterSpacing: -0.3,
          }}
        >
          {site.plan?.name ?? 'Free'}
        </div>
        {is_agency_paid ? (
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
            }}
            data-testid="agency-paid-label"
          >
            {agency_name !== null ? `PAID BY ${agency_name.toUpperCase()}` : 'PAID BY AGENCY'}
          </div>
        ) : null}
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          {site.site_id}
        </div>
      </div>
      {site.pending !== null ? (
        <p
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            marginTop: 6,
            lineHeight: 1.5,
          }}
        >
          Scheduled change: {site.pending.plan.name} on{' '}
          {new Date(site.pending.effective_at).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
          .
        </p>
      ) : null}
      <div style={{ marginTop: 16, display: 'grid', gap: 14 }}>
        <MeterRow meter={site.page_loads} />
        <MeterRow meter={site.orders} />
      </div>
    </Card>
  );
}

function MeterRow({ meter }: { meter: PlanUsageMeter }): JSX.Element {
  const C = useC();
  const label = DIMENSION_LABEL[meter.dimension];
  const usage_str = formatCount(meter.usage);
  const limit_str = meter.limit === null ? 'unlimited' : formatCount(meter.limit);
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          marginBottom: 6,
        }}
      >
        <span>{label}</span>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
          }}
        >
          {usage_str} / {limit_str}
        </span>
      </div>
      <Bar meter={meter} />
      {meter.state === 'over' && meter.limit !== null ? (
        <p
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            marginTop: 6,
            lineHeight: 1.5,
          }}
        >
          Over the plan's {label.toLowerCase()} this month. Nothing is blocked.
        </p>
      ) : null}
    </div>
  );
}

function Bar({ meter }: { meter: PlanUsageMeter }): JSX.Element {
  const C = useC();
  if (meter.limit === null) {
    return (
      <div
        style={{
          height: 4,
          background: C.border,
          borderRadius: 2,
        }}
      />
    );
  }
  const pct = Math.max(0, Math.min(100, meter.percent ?? 0));
  const color =
    meter.state === 'over'
      ? '#FF6B47'
      : meter.state === 'near'
        ? '#FFB627'
        : '#D9FF3F';
  return (
    <div
      style={{
        height: 4,
        background: C.border,
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          width: `${pct}%`,
          background: color,
        }}
      />
    </div>
  );
}

function formatCount(n: number): string {
  if (n >= 1_000_000 && n % 100_000 === 0) {
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (n >= 1_000 && n % 1_000 === 0) {
    return `${n / 1_000}K`;
  }
  return n.toLocaleString('en-US');
}
